import { useState, useRef, useEffect, useCallback, type FC } from 'react';
import { createPortal } from 'react-dom';
import DefaultContext from './Context';
import Wrapper from './Wrapper';
import Transition from './Transition';
import { positions, types } from './options';
import { groupBy } from './helpers';
import type { AlertContextType, AlertType, AlertOptionsType, ProviderProps } from './types';

const AlertProvider: FC<ProviderProps> = ({
  children,
  offset = '10px',
  position = positions.TOP_CENTER,
  timeout = 0,
  type = types.INFO,
  containerStyle = { zIndex: 100 },
  template: AlertComponent,
  context: AlertContextProp = DefaultContext, // Renamed to avoid conflicts
  ...props
}): JSX.Element => {
  const root = useRef<HTMLDivElement | null>(null);
  const timersId = useRef<number[]>([]);
  const [alerts, setAlerts] = useState<AlertType[]>([]);

  useEffect(() => {
    const rootDiv = document.createElement('div');
    rootDiv.id = '__react-alert__';
    document.body.appendChild(rootDiv);
    root.current = rootDiv;
    const timersIdRef = timersId.current;

    return (): void => {
      timersIdRef.forEach(clearTimeout);
      if (root.current) document.body.removeChild(root.current);
    };
  }, []);

  const remove = useCallback((alert: AlertType): void => {
    setAlerts(currentAlerts => {
      const lengthBeforeRemove = currentAlerts.length;
      const filteredAlerts = currentAlerts.filter(a => a.id !== alert.id);

      if (lengthBeforeRemove > filteredAlerts.length && alert.options.onClose) {
        alert.options.onClose();
      }

      return filteredAlerts;
    });
  }, []);

  const removeAll = useCallback((): void => {
    alerts.forEach(remove);
  }, [alerts, remove]);

  const show = useCallback(
    (
      message = '',
      options: AlertOptionsType = { type: types.INFO as keyof typeof types }
    ): AlertType => {
      const id = Math.random().toString(36).substring(2, 11);

      const alertOptions: AlertOptionsType = {
        position: options.position || position || positions.TOP_CENTER, // Provide a default!
        timeout: options.timeout || timeout,
        ...options,
      };

      const alert: AlertType = {
        id,
        message,
        options: alertOptions,
        close: () => remove(alert),
        style: {
          margin: offset,
          pointerEvents: 'all',
        },
      };

      if (alert.options.timeout) {
        const timerId = window.setTimeout(() => {
          // Use window.setTimeout for correct type
          remove(alert);

          const index = timersId.current.indexOf(timerId);
          if (index > -1) {
            timersId.current.splice(index, 1);
          }
        }, alert.options.timeout);

        timersId.current.push(timerId);
      }

      setAlerts(state => [...state, alert]);
      if (alert.options.onOpen) alert.options.onOpen();

      return alert;
    },
    [position, remove, timeout, type]
  );

  const success = useCallback(
    (
      message = '',
      options: AlertOptionsType = { type: types.SUCCESS as keyof typeof types }
    ): AlertType => show(message, options),
    [show]
  );

  const error = useCallback(
    (
      message = '',
      options: AlertOptionsType = { type: types.ERROR as keyof typeof types }
    ): AlertType => show(message, options),
    [show]
  );

  const info = useCallback(
    (
      message = '',
      options: AlertOptionsType = { type: types.INFO as keyof typeof types }
    ): AlertType => show(message, options),
    [show]
  );

  const alertContext: AlertContextType = {
    alerts,
    show,
    remove,
    removeAll,
    success,
    error,
    info,
  };

  const alertsByPosition = groupBy(alerts, alert => {
    const position = alert.options?.position;
    return position ?? positions.TOP_CENTER; // Provide a default position if undefined
  });

  return (
    <AlertContextProp.Provider value={alertContext}>
      {children}
      {root.current &&
        createPortal(
          <>
            {Object.keys(positions).map(key => {
              const pos = positions[key as keyof typeof positions];
              return (
                <Wrapper key={pos} options={{ position: pos, containerStyle }} {...props}>
                  {alertsByPosition[pos]?.map(
                    (
                      alert // Optional chaining here as well
                    ) => (
                      <Transition key={alert.id} type='fade' in={true} timeout={250}>
                        <AlertComponent
                          style={{
                            margin: offset,
                            pointerEvents: 'all',
                          }}
                          {...alert}
                        />
                      </Transition>
                    )
                  )}
                </Wrapper>
              );
            })}
          </>,
          root.current
        )}
    </AlertContextProp.Provider>
  );
};

export default AlertProvider;
